import React from "react";
import { Paper, Box, Typography, Button, CardMedia } from "@mui/material";
import { PlayArrow, PictureAsPdf, InsertDriveFile } from "@mui/icons-material";
import whatsappBg from "../../assets/wa_bg.png";

const SimpleTemplatePreview = ({ template, media }) => {
  const renderMediaPreview = () => {
    if (!media?.url) return null;
    const { url, type } = media;

    switch (type) {
      case "image":
        return (
          <CardMedia
            component="img"
            image={url}
            alt="Template media"
            sx={{
              width: "100%",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              objectFit: "cover",
              maxHeight: 200,
            }}
          />
        );

      case "video":
        return (
          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="video"
              src={url}
              sx={{
                width: "100%",
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                maxHeight: 200,
                objectFit: "cover",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0,0,0,0.6)",
                borderRadius: "50%",
                p: 1,
              }}
            >
              <PlayArrow sx={{ color: "white", fontSize: 32 }} />
            </Box>
          </Box>
        );

      case "pdf":
        return (
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              bgcolor: "grey.100",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <PictureAsPdf sx={{ fontSize: 36, color: "error.main", mr: 2 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Document.pdf
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF Document
              </Typography>
            </Box>
          </Box>
        );

      default:
        return (
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              bgcolor: "grey.100",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <InsertDriveFile sx={{ fontSize: 36, color: "grey.500", mr: 2 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                File Attachment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {type || "Unknown type"}
              </Typography>
            </Box>
          </Box>
        );
    }
  };

  const renderBodyText = () => {
    const bodyComponent = template.components?.find(
      (comp) => comp.type === "body"
    );
    if (!bodyComponent) return null;

    let text = bodyComponent.text;
    if (bodyComponent.example?.body_text) {
      bodyComponent.example.body_text.forEach((example, index) => {
        text = text.replace(`{{${index + 1}}}`, example);
      });
    }

    return (
      <Typography
        variant="body2"
        sx={{ color: "text.primary", lineHeight: 1.4, p: 2 }}
      >
        {text}
      </Typography>
    );
  };

  const renderFooter = () => {
    const footerComponent = template.components?.find(
      (comp) => comp.type === "footer"
    );
    if (!footerComponent) return null;

    return (
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          px: 2,
          pb: 1,
        }}
      >
        {footerComponent.text}
      </Typography>
    );
  };

  const renderButtons = () => {
    const buttonComponent = template.components?.find(
      (comp) => comp.type === "buttons"
    );
    if (!buttonComponent?.buttons) return null;

    return (
      <Box sx={{ borderTop: "1px solid", borderColor: "grey.200" }}>
        {buttonComponent.buttons.map((button, index) => (
          <Button
            key={index}
            variant="text"
            size="small"
            fullWidth
            sx={{
              textTransform: "none",
              borderBottom: "1px solid",
              borderColor: "grey.200",
              color: "primary.main",
              borderRadius: 0,
              py: 1,
            }}
            onClick={() => console.log("Button clicked:", button)}
          >
            {button.text}
          </Button>
        ))}
      </Box>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: 320,
        minHeight: 600,
        borderRadius: 3,
        overflow: "hidden",
        background: `url(${whatsappBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Fake status bar */}
      <Box
        sx={{
          height: 24,
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography variant="caption">9:41</Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Typography variant="caption">📶</Typography>
          <Typography variant="caption">🔋</Typography>
        </Box>
      </Box>

      {/* WhatsApp message bubble */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            maxWidth: "85%",
            overflow: "hidden",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {renderMediaPreview()}
          {renderBodyText()}
          {renderFooter()}
          {renderButtons()}
        </Box>
      </Box>
    </Paper>
  );
};

export default SimpleTemplatePreview;
